import { type NFTCollectibleDefinition } from '@capsicum/common';
import { useCallback } from 'react';

import { useOngoingUserParticipationsQuery } from '../../locker-room/data-access/queries/useOngoingUserParticipationsQuery';
import { type LockerRoomCampaignReward, type LockerRoomUnlock } from '../../locker-room/models/LockerRoomOptionType';
import { usePartners } from '../../partners/hooks/usePartners';
import { type MyNft } from '../models/myNfts';
import { mapPartnerToNftPartner } from '../util/mapPartnerToNftPartner';

/**
 * @returns sorted list of nfts owned by user and flexible NFT participations filtered by partner
 */

export const useMyOngoingParticipationsNftsByPartner = (partnerId: string) => {
	const { getPartnerBy } = usePartners();

	return useOngoingUserParticipationsQuery(
		useCallback(
			(data: Array<LockerRoomUnlock> | undefined) => {
				const nfts =
					data?.reduce((acc: Array<MyNft>, currentParticipation) => {
						if (
							currentParticipation.rewards?.[0]?.reward?.nft?.summary?.partnerId === partnerId &&
							currentParticipation?.status === 'accepted'
						) {
							const stackingClosedAt = currentParticipation?.challenge?.campaign?.stackingClosedAt || '';
							const reward: LockerRoomCampaignReward | undefined = currentParticipation.rewards?.[0];
							const partner = mapPartnerToNftPartner(getPartnerBy(p => p.id === partnerId));

							const rewardNft = reward?.reward?.nft as NFTCollectibleDefinition;

							const myNft = {
								optionType: currentParticipation.optionType,
								partner,
								summary: {
									...rewardNft.summary,
									nftId: reward?.id
								},
								unlockingDate: stackingClosedAt,
								userParticipationId: currentParticipation.id
							} as MyNft;

							acc.push(myNft);
						}

						return acc;
					}, []) || [];

				return nfts;
			},
			[partnerId, getPartnerBy]
		),
		{
			direction: 'desc',
			orderBy: 'createdAt',
			'rewardTypes[]': ['nft']
		}
	);
};
